import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../../utils/api';

const statusOptions = [
  { value: 'a_la_espera', label: 'A la espera' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'finalizado', label: 'Finalizado' }
];
const tipoOptions = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'interno', label: 'Interno' }
];
const prioridadOptions = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];

const emptyForm = () => ({
  titulo: '',
  estado: 'a_la_espera',
  fecha_inicio: '',
  fecha_fin: '',
  comentarios: '',
  assigned_user_ids: [],
  tipo: 'cliente',
  prioridad: 'media',
  presupuesto: '',
  notas: '',
  links: ''
});

// Modal reutilizable para crear un proyecto con el cliente ya fijado (no seleccionable).
// Se usa desde la ficha de cliente (tab Proyectos) y desde el flujo de lead ganado.
const ProjectCreateModal = ({ open, clientId, clientName, onClose, onCreated }) => {
  const token = localStorage.getItem('token');
  const [form, setForm] = useState(emptyForm());
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setError('');
    (async () => {
      try {
        const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.ok && Array.isArray(data)) setUsers(data);
      } catch (_) { /* noop */ }
    })();
  }, [open, token]);

  const userOptions = useMemo(() => users.map((u) => ({ value: u.id, label: u.name || u.email })), [users]);

  if (!open) return null;

  const save = async () => {
    if (!form.titulo.trim()) { setError('El título es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        client_id: clientId || null,
        presupuesto: form.presupuesto === '' ? null : Number(form.presupuesto)
      };
      const res = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo crear el proyecto');
      if (onCreated) onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Nuevo proyecto{clientName ? ` · ${clientName}` : ''}</h5>
            <button className="btn-close" type="button" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Título *</label>
                <input className="form-control" value={form.titulo} autoFocus onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={(e) => setForm((p) => ({ ...p, estado: e.target.value }))}>
                  {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Presupuesto (EUR)</label>
                <input type="number" step="0.01" min="0" className="form-control" value={form.presupuesto} onChange={(e) => setForm((p) => ({ ...p, presupuesto: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))}>
                  {tipoOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Prioridad</label>
                <select className="form-select" value={form.prioridad} onChange={(e) => setForm((p) => ({ ...p, prioridad: e.target.value }))}>
                  {prioridadOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Inicio</label>
                <input type="date" className="form-control" value={form.fecha_inicio} onChange={(e) => setForm((p) => ({ ...p, fecha_inicio: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Fin</label>
                <input type="date" className="form-control" value={form.fecha_fin} onChange={(e) => setForm((p) => ({ ...p, fecha_fin: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label">Usuarios asignados</label>
                <Select
                  isMulti
                  options={userOptions}
                  value={userOptions.filter((o) => form.assigned_user_ids.includes(o.value))}
                  onChange={(opts) => setForm((p) => ({ ...p, assigned_user_ids: opts ? opts.map((o) => o.value) : [] }))}
                  placeholder="Selecciona uno o varios"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Comentarios</label>
                <textarea className="form-control" rows="3" value={form.comentarios} onChange={(e) => setForm((p) => ({ ...p, comentarios: e.target.value }))} placeholder="Notas internas del proyecto" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Notas rápidas</label>
                <textarea className="form-control" rows="3" value={form.notas} onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))} placeholder="Notas breves" />
              </div>
              <div className="col-12">
                <label className="form-label">Links</label>
                <textarea className="form-control" rows="2" value={form.links} onChange={(e) => setForm((p) => ({ ...p, links: e.target.value }))} placeholder="Pega aquí URLs" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" type="button" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" type="button" disabled={saving} onClick={save}>{saving ? 'Creando...' : 'Crear proyecto'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreateModal;
